// src/components/FileDropZone.jsx
// Drag-and-drop uploader backed by Firebase Storage.
//
// THE BUG (original):
// When multiple files upload concurrently, each upload's onComplete callback
// called onUrlsChange([...existingUrls, ...newUrls]) — but existingUrls was
// captured at upload-start time via a ref, so every callback saw the SAME
// snapshot of existingUrls and produced duplicates of earlier files.
//
// THE SECOND BUG (this fix):
// A feedback loop caused image duplicates even after the first fix:
//   1. Upload completes → useEffect fires → onUrlsChange([...existingUrls, ...uploadedUrls])
//   2. Parent stores the new array and passes it back as existingUrls
//   3. The completed URL now lives in BOTH existingUrls AND local `uploads` state
//   4. useEffect fires again → URL counted twice → duplicates in the list
//
// THE FIX:
// After each upload completes, a separate useEffect watches for its URL
// appearing in existingUrls (confirmation the parent has acknowledged it) and
// then removes that entry from local `uploads` state. This ensures a URL is
// never held in both places at once, breaking the feedback loop entirely.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { storage } from '../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// ── Single upload row ─────────────────────────────────────────────────────────
function UploadRow({ file, progress, url, error, onRemove }) {
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  const isJson  = file.name.endsWith('.json');
  const done    = progress === 100 && url;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 12px',
      background: error ? '#2e0a0a' : done ? '#0a2e20' : 'var(--sand-surface)',
      border: `0.5px solid ${error ? '#5f1010' : done ? '#0d4a35' : 'var(--sand-border)'}`,
      borderRadius: 'var(--radius-sm)', marginBottom: 6,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 4, flexShrink: 0,
        background: isJson ? 'var(--sand-amber-light)' : isImage ? '#1a1535' : '#0a2e20',
        border: `0.5px solid ${isJson ? 'var(--sand-amber-border)' : isImage ? '#2a2050' : '#0d4a44'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {isJson && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--sand-amber)" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
        {isImage && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>}
        {isVideo && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5eead4" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: error ? '#fca5a5' : 'var(--sand-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.name}
        </div>
        {error ? (
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#fca5a5', marginTop: 2 }}>{error}</div>
        ) : done ? (
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#5eead4', marginTop: 2 }}>Uploaded ✓</div>
        ) : (
          <div style={{ marginTop: 4 }}>
            <div style={{ height: 3, background: 'var(--sand-border)', borderRadius: 2 }}>
              <div style={{ height: 3, borderRadius: 2, background: 'var(--sand-amber)', width: `${progress}%`, transition: 'width 0.2s' }} />
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)', marginTop: 2 }}>{Math.round(progress)}%</div>
          </div>
        )}
      </div>

      {isImage && url && (
        <img src={url} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, border: '0.5px solid var(--sand-border)', flexShrink: 0 }} />
      )}

      <button onClick={() => onRemove(url || file.name)} title="Remove"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sand-hint)', fontSize: 16, lineHeight: 1, padding: '2px 4px', flexShrink: 0 }}
        onMouseEnter={e => e.currentTarget.style.color = '#fca5a5'}
        onMouseLeave={e => e.currentTarget.style.color = 'var(--sand-hint)'}
      >×</button>
    </div>
  );
}

// ── Existing URL row ──────────────────────────────────────────────────────────
function ExistingRow({ url, onRemove }) {
  const pathPart = url.split('?')[0].toLowerCase();
  const isImage = /\.(jpg|jpeg|png|gif|webp)/.test(pathPart);
  const isVideo = /\.(mp4|webm|mov)/.test(pathPart);
  const isJson  = /\.json/.test(pathPart);
  const short   = decodeURIComponent(url.split('?')[0].split('/').pop()).replace(/^\d+_/, '');

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 12px',
      background: '#0a2e20', border: '0.5px solid #0d4a35',
      borderRadius: 'var(--radius-sm)', marginBottom: 6,
    }}>
      <div style={{ width: 28, height: 28, borderRadius: 4, flexShrink: 0, background: '#0d2e29', border: '0.5px solid #0d4a35', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#5eead4' }}>
        {isJson ? '{}' : isImage ? '🖼' : isVideo ? '▶' : '?'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--sand-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{short}</div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#5eead4', marginTop: 1 }}>Saved ✓</div>
      </div>
      {isImage && <img src={url} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, border: '0.5px solid var(--sand-border)', flexShrink: 0 }} />}
      <button onClick={() => onRemove(url)} title="Remove"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sand-hint)', fontSize: 16, lineHeight: 1, padding: '2px 4px', flexShrink: 0 }}
        onMouseEnter={e => e.currentTarget.style.color = '#fca5a5'}
        onMouseLeave={e => e.currentTarget.style.color = 'var(--sand-hint)'}
      >×</button>
    </div>
  );
}

// ── Main FileDropZone ─────────────────────────────────────────────────────────
export default function FileDropZone({
  storagePath, accept = '*/*', multiple = false,
  label = 'Drop file here', hint,
  existingUrls = [], onUrlsChange, required = false,
}) {
  // uploads: array of { id, file, progress, url, error }
  // Each upload has a stable unique id so state updates don't collide.
  // Once an upload's URL is acknowledged by the parent (appears in existingUrls),
  // the entry is removed from this array so no URL is ever counted twice.
  const [uploads, setUploads] = useState([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();
  const idCounter = useRef(0);

  // ── Notify parent when a new upload completes ─────────────────────────────
  // Only fires when uploads changes, and only reports URLs not already in
  // existingUrls to avoid double-counting during the render cycle before
  // the graduated-to-existing cleanup (below) has run.
  useEffect(() => {
    const existingSet = new Set(existingUrls);
    const newUrls = uploads.filter(u => u.url && !existingSet.has(u.url)).map(u => u.url);
    if (newUrls.length > 0) {
      onUrlsChange?.([...existingUrls, ...newUrls]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads]);

  // ── Graduate completed uploads to existingUrls ────────────────────────────
  // Once the parent has acknowledged a URL (it appears in existingUrls), remove
  // it from local uploads so it's only rendered once via ExistingRow.
  useEffect(() => {
    const existingSet = new Set(existingUrls);
    setUploads(prev => prev.filter(u => !u.url || !existingSet.has(u.url)));
  }, [existingUrls]);

  function uploadFile(file) {
    const id   = ++idCounter.current;
    const path = `${storagePath}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);

    setUploads(prev => [...prev, { id, file, progress: 0, url: null, error: null }]);

    task.on('state_changed',
      snapshot => {
        const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploads(prev => prev.map(u => u.id === id ? { ...u, progress: pct } : u));
      },
      err => {
        setUploads(prev => prev.map(u => u.id === id ? { ...u, error: err.message } : u));
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setUploads(prev => prev.map(u => u.id === id ? { ...u, progress: 100, url } : u));
      }
    );
  }

  function handleFiles(files) {
    const list = multiple ? Array.from(files) : [files[0]];
    list.forEach(uploadFile);
  }

  const onDrop = useCallback(e => {
    e.preventDefault(); setDragging(false);
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  }, []);

  function removeExisting(url) {
    const next = existingUrls.filter(u => u !== url);
    const existingSet = new Set(next);
    const newUrls = uploads.filter(u => u.url && !existingSet.has(u.url)).map(u => u.url);
    onUrlsChange?.([...next, ...newUrls]);
  }

  function removeUpload(id) {
    setUploads(prev => prev.filter(u => u.id !== id));
  }

  const hasFiles = existingUrls.length > 0 || uploads.length > 0;
  const showRequired = required && existingUrls.length === 0 && !uploads.some(u => u.url);

  return (
    <div>
      {existingUrls.map(url => (
        <ExistingRow key={url} url={url} onRemove={removeExisting} />
      ))}

      {uploads.map(u => (
        <UploadRow
          key={u.id} file={u.file} progress={u.progress}
          url={u.url} error={u.error}
          onRemove={() => removeUpload(u.id)}
        />
      ))}

      {(!hasFiles || multiple) && (
        <div
          onClick={() => inputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          style={{
            border: `1.5px dashed ${showRequired ? '#fca5a5' : dragging ? 'var(--sand-amber)' : 'var(--sand-border-strong)'}`,
            borderRadius: 'var(--radius-md)', padding: '20px 16px', textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? 'var(--sand-amber-light)' : showRequired ? '#2e0a0a' : 'var(--sand-bg)',
            transition: 'all 0.15s',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke={dragging ? 'var(--sand-amber)' : 'var(--sand-hint)'}
            strokeWidth="1.5" strokeLinecap="round"
            style={{ margin: '0 auto 8px', display: 'block' }}>
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: 13, color: dragging ? 'var(--sand-amber)' : 'var(--sand-muted)', marginBottom: 4 }}>
            {dragging ? 'Drop to upload' : label}
          </div>
          {hint && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)', marginTop: 4 }}>{hint}</div>}
          {showRequired && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#fca5a5', marginTop: 6 }}>Required — please upload a .json file</div>}
        </div>
      )}

      <input ref={inputRef} type="file" accept={accept} multiple={multiple} style={{ display: 'none' }}
        onChange={e => { if (e.target.files.length) handleFiles(e.target.files); e.target.value = ''; }}
      />
    </div>
  );
}
